import type { OnChanges, SimpleChanges, TrackByFunction } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  output,
} from '@angular/core';

import { trackByProp } from '@recruitee/common';
import type { Attribute, AttributeListData } from '@recruitee/http-types';

import { IconButtonComponent } from '../../common/button/components/icon-button/icon-button.component';
import { GroupComponent } from '../../common/group/group.component';
import { IconComponent } from '../../common/icon/icon.component';
import { TagComponent } from '../../common/tag/tag.component';
import { OverlayOriginDirective } from '../../core/overlay/overlay-directives';
import { AttributesComponent } from '../attributes.component';
import type { TranslationsContextType } from '../attributes.types';

@Component({
  selector: 'rt-input-attributes',
  templateUrl: 'input-attributes.component.html',
  styleUrls: ['./input-attributes.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AttributesComponent,
    GroupComponent,
    IconButtonComponent,
    IconComponent,
    OverlayOriginDirective,
    TagComponent,
  ],
})
export class InputAttributesComponent implements OnChanges {
  @Input() public selectedAttributes: Attribute[] = [];
  @Input() public filteredAttributes: Attribute[] = [];
  @Input() public placeholder: string;
  @Input() public hasClearButton: boolean = true;
  @Input() public noRightPadding: boolean = false;
  @Input() public disabled: boolean = false;
  @Input() public max: number | null = null;
  @Input() public translationsContext: TranslationsContextType;

  public save = output<Attribute>();
  public query = output<string>();
  public deleteAttribute = output<Attribute>();
  public clearAll = output<void>();

  public toggle: boolean = false;
  public trackById: TrackByFunction<Attribute> = trackByProp<Attribute>({ prop: 'id' });
  public suggestionsData: AttributeListData = { isLoading: false, meta: null, rows: [] };

  constructor(protected cdRef: ChangeDetectorRef) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if ('filteredAttributes' in changes) {
      this.suggestionsData = {
        meta: {
          page: 1,
          limit: this.filteredAttributes.length,
          totalCount: this.filteredAttributes.length,
        },
        rows: this.filteredAttributes,
        isLoading: false,
      };
    }
  }

  public onSave(attr: Attribute): void {
    if (this.max === null || this.max > this.selectedAttributes.length) {
      this.save.emit(attr);
    }

    this.toggle = !this.toggle;
    this.cdRef.detectChanges();
  }

  public handleClearAll(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.disabled) {
      this.clearAll.emit();
    }
  }
}
